"use client";

import { useEffect, useMemo, useRef } from "react";
import { Button } from "./button";
import { Icon, ArrowRight, TriangleAlert } from "./icon";
import { clientTrack } from "@/lib/analytics/client";
import type { Track } from "@/lib/analytics/track";
import { emitQuotaWarningShown, quotaLimitToQuotaType } from "@/lib/analytics/paywall";
import { webAppContext } from "@/lib/analytics/surface";
import type { AnalyticsContext } from "@/lib/analytics/envelope";
import type { QuotaLimit } from "@/lib/billing/quota";

/**
 * Quota-warning nudge (M12-B, §6 Stage E "quota_warning_shown"). The PRE-gate
 * signal — shown as an account approaches a storage limit, BEFORE the paywall
 * blocks anything — so the funnel measures how many warned users upgrade before
 * they hit the wall. On mount it fires the canonical
 * `quota_warning_shown { quota_type, usage, limit }` exactly once.
 *
 * Token-only + brand-free (name arrives as a prop from the white-label theme);
 * no dark pattern (no fake scarcity), and it never obstructs the cancel path.
 * `track`/`ctx` are injectable so tests drive the emission with a mock; in the
 * app it binds to the browser sink like the other editor beacons.
 */

export interface QuotaWarningProps {
  /** The storage limit being approached. */
  limit: QuotaLimit;
  /** Current usage count. */
  usage: number;
  /** The plan cap (null = unlimited — the nudge should not be shown). */
  cap: number | null;
  /** White-label brand name — from getTheme, never a literal. */
  brandName: string;
  /** Optional upgrade nudge CTA. */
  onUpgrade?: () => void;
  /** Anon/user session id used to build the client analytics context. */
  anonSessionId?: string;
  userId?: string;
  /** Emission seam (defaults to the browser sink); tests inject a mock. */
  track?: Track;
  /** Analytics context override (defaults to a web_app context from the ids). */
  ctx?: AnalyticsContext;
}

function nudgeCopy(limit: QuotaLimit, usage: number, cap: number | null, brandName: string): { title: string; body: string } {
  const remaining = cap === null ? null : Math.max(0, cap - usage);
  switch (limit) {
    case "saved_maps":
      return {
        title: cap !== null ? `${usage} of ${cap} saved maps used` : "You're using your saved maps",
        body:
          remaining === 0
            ? `You've used every saved map on your ${brandName} plan. Upgrade to keep saving maps.`
            : `You have ${remaining} saved map${remaining === 1 ? "" : "s"} left on your ${brandName} plan.`,
      };
    case "live_maps":
      return {
        title: cap !== null ? `${usage} of ${cap} live maps used` : "You're using your live maps",
        body:
          remaining === 0
            ? "You've used every live map on your plan. Upgrade for more auto-refreshing maps."
            : `You have ${remaining} live map${remaining === 1 ? "" : "s"} left on your plan.`,
      };
    case "refresh_cadence":
    case "scheduled_refresh":
      return {
        title: "Approaching your refresh limit",
        body: "Scheduled auto-refresh is limited on your current plan. Upgrade to refresh more often.",
      };
    case "watermark":
      return {
        title: "Attribution badge on your embeds",
        body: `Embeds show the “Made with ${brandName}” badge on your current plan. Upgrade to remove it.`,
      };
    case "live_sync":
    case "crm_sync":
      return {
        title: "Approaching your live-sync limit",
        body: "Live data sync is limited on your current plan. Upgrade to connect more sources.",
      };
    case "api_access":
      return {
        title: "Approaching your API limit",
        body: "API access is limited on your current plan. Upgrade for more programmatic access.",
      };
    case "organize":
      return {
        title: "Folders & tags",
        body: "Organizing your maps into folders and tags isn't available on your current plan. Upgrade to organize your maps.",
      };
    case "private_share":
      return {
        title: "Unlisted publishing",
        body: "Upgrade to publish a shareable map without listing it in the public directory.",
      };
    case "map_points":
      return {
        title: cap !== null ? `${usage} of ${cap} locations` : "This map is over the location limit",
        body:
          cap !== null
            ? `Free maps hold up to ${cap} locations; this one has ${usage}. Upgrade to map unlimited locations per map.`
            : "Your current plan limits locations per map. Upgrade to map unlimited locations per map.",
      };
    case "satellite_basemap":
      return {
        title: "Satellite publishing requires Lite",
        body: "You can keep editing and saving this draft. Upgrade when you’re ready to publish it with satellite imagery.",
      };
  }
}

export function QuotaWarning({
  limit,
  usage,
  cap,
  brandName,
  onUpgrade,
  anonSessionId,
  userId,
  track,
  ctx,
}: QuotaWarningProps) {
  const resolvedTrack = useMemo<Track>(() => track ?? clientTrack, [track]);
  const resolvedCtx = useMemo<AnalyticsContext>(
    () => ctx ?? webAppContext({ anonSessionId, userId }),
    [ctx, anonSessionId, userId],
  );

  // Fire quota_warning_shown exactly once per mount (the pre-gate nudge appeared).
  const fired = useRef(false);
  useEffect(() => {
    if (fired.current) return;
    fired.current = true;
    emitQuotaWarningShown(resolvedTrack, resolvedCtx, {
      quotaType: quotaLimitToQuotaType(limit),
      usage,
      limit: cap,
    });
  }, [resolvedTrack, resolvedCtx, limit, usage, cap]);

  const copy = nudgeCopy(limit, usage, cap, brandName);

  return (
    <div className="pz-quota-warn" role="status" aria-live="polite">
      <span className="pz-quota-warn__glyph" aria-hidden>
        <Icon icon={TriangleAlert} size="md" />
      </span>
      <div className="pz-quota-warn__text">
        <p className="pz-quota-warn__title">{copy.title}</p>
        <p className="pz-quota-warn__body">{copy.body}</p>
      </div>
      {onUpgrade ? (
        <Button
          variant="secondary"
          size="sm"
          onClick={onUpgrade}
          className="pz-quota-warn__cta"
          iconTrailing={<Icon icon={ArrowRight} size="sm" />}
        >
          Upgrade
        </Button>
      ) : null}
    </div>
  );
}
